import React, { useRef } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor,
  Alignment,
  Autoformat,
  AutoLink,
  Autosave,
  BlockQuote,
  Bold,
  Code,
  CodeBlock,
  Essentials,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  GeneralHtmlSupport,
  Heading,
  HorizontalLine,
  HtmlEmbed,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  ListProperties,
  MediaEmbed,
  Paragraph,
  ShowBlocks,
  SourceEditing,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  TodoList,
  Underline,
} from "ckeditor5";

const editorConfig = {
  toolbar: {
    items: [
      "sourceEditing",
      "showBlocks",
      "|",
      "heading",
      "|",
      "fontSize",
      "fontFamily",
      "fontColor",
      "fontBackgroundColor",
      "|",
      "bold",
      "italic",
      "underline",
      "code",
      "|",
      "horizontalLine",
      "link",
      "mediaEmbed",
      "insertTable",
      "blockQuote",
      "codeBlock",
      "htmlEmbed",
      "|",
      "alignment",
      "|",
      "bulletedList",
      "numberedList",
      "todoList",
      "outdent",
      "indent"
    ],
    shouldNotGroupWhenFull: false
  },
  plugins: [
    Alignment,
    Autoformat,
    AutoLink,
    Autosave,
    BlockQuote,
    Bold,
    Code,
    CodeBlock,
    Essentials,
    FontBackgroundColor,
    FontColor,
    FontFamily,
    FontSize,
    GeneralHtmlSupport,
    Heading,
    HorizontalLine,
    HtmlEmbed,
    Indent,
    IndentBlock,
    Italic,
    Link,
    List,
    ListProperties,
    MediaEmbed,
    Paragraph,
    ShowBlocks,
    SourceEditing,
    Table,
    TableCaption,
    TableCellProperties,
    TableColumnResize,
    TableProperties,
    TableToolbar,
    TextTransformation,
    TodoList,
    Underline
  ],
  fontFamily: {
    supportAllValues: true
  },
  fontSize: {
    options: [10, 12, 14, "default", 18, 20, 22],
    supportAllValues: true
  },
  heading: {
    options: [
      {
        model: "paragraph",
        title: "Paragraph",
        class: "ck-heading_paragraph"
      },
      {
        model: "heading1",
        view: "h1",
        title: "Heading 1",
        class: "ck-heading_heading1"
      },
      {
        model: "heading2",
        view: "h2",
        title: "Heading 2",
        class: "ck-heading_heading2"
      },
      {
        model: "heading3",
        view: "h3",
        title: "Heading 3",
        class: "ck-heading_heading3"
      },
      {
        model: "heading4",
        view: "h4",
        title: "Heading 4",
        class: "ck-heading_heading4"
      },
      {
        model: "heading5",
        view: "h5",
        title: "Heading 5",
        class: "ck-heading_heading5"
      },
      {
        model: "heading6",
        view: "h6",
        title: "Heading 6",
        class: "ck-heading_heading6"
      }
    ]
  },
  htmlSupport: {
    allow: [
      {
        name: /^.*$/,
        styles: true,
        attributes: true,
        classes: true
      }
    ]
  },
  licenseKey: gon.ckeditor_license_key,
  link: {
    addTargetToExternalLinks: true,
    defaultProtocol: "https://",
    decorators: {
      toggleDownloadable: {
        mode: "manual",
        label: "Downloadable",
        attributes: {
          download: "file"
        }
      }
    }
  },
  list: {
    properties: {
      styles: true,
      startIndex: true,
      reversed: true
    }
  },
  placeholder: "Type or paste your content here.",
  table: {
    contentToolbar: ["tableColumn", "tableRow", "mergeTableCells", "tableProperties", "tableCellProperties"]
  }
};

// The catch-all htmlSupport regex does not cover <script>/<style> — they must
// be allowed by name so source-mode embeds (e.g. booking widgets) survive
// editing. This is opt-in per instance so the allowance stays scoped to the
// fields that actually need it rather than every CKEditor on the app.
const SCRIPT_EMBED_ALLOW = [
  { name: "script", attributes: true },
  { name: "style", attributes: true }
];

const CKEditorContainer = ({
  content,
  setContent,
  containerStyle,
  allowScriptEmbeds = false,
}) => {
  const editorRef = useRef(null);

  const config = allowScriptEmbeds
    ? {
        ...editorConfig,
        htmlSupport: {
          ...editorConfig.htmlSupport,
          allow: [...editorConfig.htmlSupport.allow, ...SCRIPT_EMBED_ALLOW]
        }
      }
    : editorConfig;

  return (
    <div style={containerStyle} ref={editorRef}>
      <CKEditor
        editor={ClassicEditor}
        config={config}
        data={content}
        onChange={(_event, editor) => {
          const data = editor.getData();
          setContent(data);
        }}
      />
    </div>
  );
};

export default CKEditorContainer;
